import {
  PowermailPi1,
  SectionAccordion,
  SectionCardGrid,
  SectionCta,
  SectionCtaSingle,
  SectionFeature,
  SectionGallery,
  SectionHero,
  SectionHeroHome,
  SectionInfo,
  SectionLink,
  SectionMap,
  SectionPartners,
  SectionSlider,
  SectionSliderHome,
  SectionVideo,
  SectionWeather,
  SectionWebcams,
  SpotHeadlessDownload,
  SpotHeadlessHtml,
  SpotHeadlessTeam,
  SpotSanityAccomodation,
  SpotSanityContentTable,
  SpotSanityContentText,
  SpotSanityCulture,
  SpotSanityEatDrink,
  SpotSanityEventList,
  SpotSanityFreetime,
  SpotSanityNature,
  SpotSanityReports,
  SpotSanityService,
  SpotSanityShopping,
  SpotSanitySport,
  SpotSanitySportServices,
  SpotSanityStory,
  SpotSanityTour
} from '@/ui/Sections'

import {
  toSectionAccordion,
  toSectionCta,
  toSectionCtaSingle,
  toSectionFeature,
  toSectionGallery,
  toSectionInfo,
  toSectionLink,
  toSectionPartners,
  toSectionSlider,
  toSectionVideo
} from '@/lib/transformers'
import { Typo3Page, Typo3SectionsTypes } from '@/types'
import {
  Typo3SectionSpotHeadlessAccordion,
  Typo3SectionSpotHeadlessCtaImageVideo,
  Typo3SectionSpotHeadlessFeature,
  Typo3SectionSpotHeadlessInfoSlider,
  Typo3SectionSpotHeadlessPages,
  Typo3SectionSpotHeadlessPartners,
  Typo3SectionSpotSanityContentGallery,
  Typo3SectionSpotSanityContentShop,
  Typo3SectionSpotSanityContentTable,
  Typo3SectionSpotSanityContentVideo,
  Typo3SectionSpotSanityCulture,
  Typo3SectionSpotSanityEatDrink,
  Typo3SectionSpotSanityEventList,
  Typo3SectionSpotSanityEventTop,
  Typo3SectionSpotSanityNature,
  Typo3SectionSpotSanityService,
  Typo3SectionSpotSanityShopping,
  Typo3SectionSpotSanitySport,
  Typo3SectionSpotSanitySportServices,
  Typo3SectionSpotSanityStory,
  Typo3SectionSpotSanityTour
} from '@/types/typo3'
import { memo, Suspense } from 'react'

interface Typo3Section {
  type: Typo3SectionsTypes
  apparance?: { layout?: string }
  [key: string]: any
}

interface Props {
  data: Typo3Page & { content: Typo3Section[] }
}

const PageBuilder = ({ data }: Props) => {
  const Sections = (section: Typo3Section) => {
    const layout =
      section.isPlugin === true
        ? section?.plugin?.layout
        : section?.appearance?.layout || ''
    try {
      switch (section.type) {
        case 'spotheadless_hero':
          return <SectionHero data={section as any} pageData={data as any} />
        case 'spotheadless_herohome':
          return <SectionHeroHome data={section as any} />
        case 'spotsanity_content_text':
          return <SpotSanityContentText data={section as any} />
        case 'spotsanity_content_video': {
          const sectionData = toSectionVideo({
            section: section as Typo3SectionSpotSanityContentVideo
          })
          return <SectionVideo {...sectionData} />
        }
        case 'spotheadless_accordion': {
          const sectionData = toSectionAccordion(
            section as Typo3SectionSpotHeadlessAccordion
          )
          return <SectionAccordion {...sectionData} />
        }
        case 'spotsanity_content_gallery': {
          const sectionData = toSectionGallery(
            section as Typo3SectionSpotSanityContentGallery
          )
          return <SectionGallery {...sectionData} />
        }
        case 'spotheadless_ctaimagevideo': {
          const sectionData = toSectionCtaSingle(
            section as Typo3SectionSpotHeadlessCtaImageVideo
          )
          return <SectionCtaSingle {...sectionData} />
        }
        case 'spotsanity_content_shop':
          if (layout === 'slide') {
            const sectionData = toSectionFeature(
              section as Typo3SectionSpotSanityContentShop
            )
            return <SectionFeature {...sectionData} variant="shop" />
          } else if (layout === 'cta') {
            const sectionData = toSectionCta(
              section as Typo3SectionSpotSanityContentShop
            )
            return <SectionCta {...sectionData} />
          } else {
            const sectionData = toSectionLink(
              section as Typo3SectionSpotSanityContentShop
            )
            return <SectionLink {...sectionData} variant="shop" />
          }
        case 'spotsanity_content_table':
          return (
            <SpotSanityContentTable
              data={section as Typo3SectionSpotSanityContentTable}
            />
          )
        case 'spotheadless_infoslider': {
          const sectionData = toSectionInfo({
            section: section as Typo3SectionSpotHeadlessInfoSlider
          })
          return <SectionInfo {...sectionData} />
        }
        case 'spotsanity_webcam':
          return <SectionWebcams data={section as any} />
        case 'spotheadless_download':
          return <SpotHeadlessDownload data={section as any} />
        case 'spotheadless_map':
          return <SectionMap data={section as any} />
        case 'spotsanity_weather':
          return <SectionWeather data={section as any} />
        case 'spotsanity_reports':
          return (
            <Suspense>
              <SpotSanityReports data={section as any} />
            </Suspense>
          )

        case 'spotheadless_pages': {
          const sectionData = toSectionLink(
            section as Typo3SectionSpotHeadlessPages
          )

          if (section?.appearance?.layout === 'card-grid') {
            return <SectionCardGrid {...sectionData} />
          } else {
            return <SectionLink {...sectionData} variant="link" />
          }
        }
        case 'spotsanity_tour':
          return <SpotSanityTour data={section as Typo3SectionSpotSanityTour} />
        case 'spotsanity_eventlist':
          return (
            <SpotSanityEventList
              data={section as Typo3SectionSpotSanityEventList}
            />
          )
        case 'spotsanity_eventtop': {
          const sectionData = toSectionFeature(
            section as Typo3SectionSpotSanityEventTop
          )
          return <SectionFeature {...sectionData} variant="topEvent" />
        }
        case 'spotsanity_cultureandarchitecture':
          return (
            <SpotSanityCulture
              data={section as Typo3SectionSpotSanityCulture}
            />
          )
        case 'spotsanity_eatdrink':
          return (
            <SpotSanityEatDrink
              data={section as Typo3SectionSpotSanityEatDrink}
            />
          )
        case 'spotsanity_sportinfrastructure':
          return (
            <SpotSanitySport data={section as Typo3SectionSpotSanitySport} />
          )
        case 'spotsanity_sportservice':
          return (
            <SpotSanitySportServices
              data={section as Typo3SectionSpotSanitySportServices}
            />
          )
        case 'spotsanity_freetime':
          return <SpotSanityFreetime data={section as any} />
        case 'spotsanity_nature':
          return (
            <SpotSanityNature data={section as Typo3SectionSpotSanityNature} />
          )
        case 'spotsanity_accommodation':
          return <SpotSanityAccomodation data={section as any} />
        case 'spotsanity_shopping':
          return (
            <SpotSanityShopping
              data={section as Typo3SectionSpotSanityShopping}
            />
          )
        case 'spotheadless_partner': {
          const sectionData = toSectionPartners(
            section as Typo3SectionSpotHeadlessPartners
          )
          return <SectionPartners {...sectionData} />
        }
        case 'spotheadless_html':
          return <SpotHeadlessHtml data={section as any} />
        case 'spotheadless_team':
          return <SpotHeadlessTeam data={section as any} />
        case 'spotsanity_service':
          return (
            <SpotSanityService
              data={section as Typo3SectionSpotSanityService}
            />
          )
        case 'spotsanity_story':
          return (
            <SpotSanityStory
              data={section as Typo3SectionSpotSanityStory}
              pageData={data}
            />
          )
        case 'spotheadless_feature':
          switch (layout) {
            case 'slide-right': {
              const sectionData = toSectionSlider(
                section as Typo3SectionSpotHeadlessFeature
              )
              return <SectionSlider {...sectionData} />
            }
            case 'slide':
              return <SectionSliderHome data={section as any} />
            default: {
              const sectionData = toSectionFeature(
                section as Typo3SectionSpotHeadlessFeature
              )
              return <SectionFeature {...sectionData} />
            }
          }
        case 'powermail_pi1':
          return <PowermailPi1 data={section as any} />
      }
    } catch (error) {
      console.log(
        `getComponentFromSection: error in paragraph ${section.type}: ${error}`
      )
      return null
    }
  }

  return (
    <>
      {data?.content?.map((section: Typo3Section, i: number) => (
        <Sections key={i?.toString()} {...section} />
      ))}
    </>
  )
}

export default memo(PageBuilder)
